import { useState, type ReactNode } from 'react';
import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { api, ifMatch, newIdempotencyKey, unwrap } from '@/api/client';
import { useCommand } from '@/app/commands';
import { orgKey } from '@/app/queryClient';
import { useOrgId } from '@/app/session';
import {
  Banner,
  Button,
  Section,
  DescriptionList,
  EmptyState,
  Field,
  IdChip,
  Menu,
  PageHeader,
  QueryState,
  Select,
  StatusDot,
  TextInput,
  Time,
  Timeline,
  Unknown,
} from '@/components/ui';
import { DataTable } from '@/components/data';
import { formatNumber } from '@/lib/format';
import { ConfirmDialog, usePagedList, type Schemas } from '@/features/settings/shared';
import { ObjectLink } from '@/app/links';
import { ActorsPage } from './ActorsPage';
import { ApiKeysPage } from './ApiKeysPage';

type Run = Schemas['AgentRun'];
type Session = Schemas['AgentSession'];
type Job = Schemas['Job'];

const RUN_KINDS: Schemas['AgentRunKind'][] = ['planner', 'communication', 'enrichment', 'readiness', 'simple_llm'];
const RUN_STATES: Schemas['AgentRunState'][] = ['queued', 'running', 'completed', 'failed', 'cancelled', 'checkpoint_lost'];
const SESSION_ROLES: Schemas['AgentSessionRole'][] = ['planner', 'communication'];
const SESSION_STATES: Schemas['AgentSessionState'][] = ['active', 'paused', 'checkpoint_lost', 'retired'];
const JOB_KINDS: Schemas['JobKind'][] = [
  'draft_initial',
  'draft_reply',
  'followup',
  'planner',
  'readiness',
  'enrichment',
  'send_mail',
  'notification_delivery',
  'daily_digest',
  'blob_scan',
  'blob_extract',
  'maintenance',
  'export',
  'migration_validate',
];
const JOB_STATES: Schemas['JobState'][] = ['pending', 'leased', 'succeeded', 'failed', 'cancelled', 'blocked'];

/** Plain names for run kinds, session roles, job kinds, states and outcomes. Shared values mean the same thing everywhere. */
const ENUM_LABELS: Record<string, string> = {
  planner: 'Planning',
  communication: 'Messaging',
  enrichment: 'Research',
  readiness: 'Readiness check',
  simple_llm: 'AI draft',
  draft_initial: 'First draft',
  draft_reply: 'Reply draft',
  followup: 'Follow up',
  send_mail: 'Send mail',
  notification_delivery: 'Send notification',
  daily_digest: 'Daily summary',
  blob_scan: 'File check',
  blob_extract: 'File reading',
  maintenance: 'Maintenance',
  export: 'Export',
  migration_validate: 'Import check',
  queued: 'Queued',
  pending: 'Waiting',
  leased: 'Running',
  running: 'Running',
  committed: 'Done',
  completed: 'Done',
  succeeded: 'Done',
  failed: 'Failed',
  cancelled: 'Cancelled',
  blocked: 'Blocked',
  budget_exhausted: 'Budget used up',
  checkpoint_lost: 'Lost progress',
  active: 'Active',
  paused: 'Paused',
  retired: 'Retired',
};

function enumLabel(v: string): string {
  return ENUM_LABELS[v] ?? v;
}

function options(values: readonly string[], all: string) {
  return [{ value: '', label: all }, ...values.map((v) => ({ value: v, label: enumLabel(v) }))];
}

// ---------------- Runs ----------------

export interface RunsSearch {
  kind?: Schemas['AgentRunKind'];
  state?: Schemas['AgentRunState'];
}

export function validateRunsSearch(s: Record<string, unknown>): RunsSearch {
  return {
    kind: RUN_KINDS.includes(s.kind as never) ? (s.kind as RunsSearch['kind']) : undefined,
    state: RUN_STATES.includes(s.state as never) ? (s.state as RunsSearch['state']) : undefined,
  };
}

export function RunsPage({ search, onSearch }: { search: RunsSearch; onSearch: (s: RunsSearch) => void }) {
  const orgId = useOrgId();
  const navigate = useNavigate();
  const { pager, query } = usePagedList<Run>(orgKey(orgId, 'runs', search), (q, signal) =>
    unwrap(api.GET('/api/v1/orgs/{orgId}/runs', { params: { path: { orgId }, query: { ...q, kind: search.kind, state: search.state } }, signal })),
  );
  const columns: ColumnDef<Run, unknown>[] = [
    { id: 'id', header: 'Run', cell: ({ row }) => <IdChip id={row.original.id} />, meta: { pinned: true } },
    { id: 'kind', header: 'Kind', cell: ({ row }) => enumLabel(row.original.kind) },
    { id: 'state', header: 'State', cell: ({ row }) => <StatusDot domain="run" value={row.original.state} /> },
    { id: 'location', header: 'Location', cell: ({ row }) => <ObjectLink kind="location" id={row.original.location_id} /> },
    { id: 'conversation', header: 'Conversation', cell: ({ row }) => <ObjectLink kind="conversation" id={row.original.conversation_id} /> },
    { id: 'session', header: 'Session', cell: ({ row }) => <ObjectLink kind="session" id={row.original.session_id} /> },
    { id: 'started', header: 'Started', cell: ({ row }) => <Time value={row.original.started_at} emptyLabel="Not started" /> },
    { id: 'ended', header: 'Ended', cell: ({ row }) => <Time value={row.original.ended_at} emptyLabel="Not ended" /> },
  ];
  return (
    <div className="stack">
      <p className="small muted">Work recorded by outside agents through the API.</p>
      <DataTable
        caption="Agent runs"
        columns={columns}
        getRowId={(r) => r.id}
        query={query}
        pager={pager}
        sort={[]}
        onSortChange={() => undefined}
        onOpenRow={(r) => void navigate({ to: '/app/settings/agents/runs/$runId', params: { runId: r.id } })}
        rowLabel={(r) => `${enumLabel(r.kind)} run ${r.id}`}
        toolbar={
          <div className="settings-filters">
            <Select
              aria-label="Kind"
              className="settings-filter"
              options={options(RUN_KINDS, 'All kinds')}
              value={search.kind ?? ''}
              onChange={(e) => onSearch({ ...search, kind: (e.target.value || undefined) as RunsSearch['kind'] })}
            />
            <Select
              aria-label="State"
              className="settings-filter"
              options={options(RUN_STATES, 'All states')}
              value={search.state ?? ''}
              onChange={(e) => onSearch({ ...search, state: (e.target.value || undefined) as RunsSearch['state'] })}
            />
          </div>
        }
        empty={<EmptyState title="No runs match." />}
      />
    </div>
  );
}

export function RunDetailPage({ runId }: { runId: string }) {
  const orgId = useOrgId();
  const key = orgKey(orgId, 'runs', 'detail', runId);
  const run = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => unwrap(api.GET('/api/v1/orgs/{orgId}/runs/{runId}', { params: { path: { orgId, runId } }, signal })),
  });
  const [cancelOpen, setCancelOpen] = useState(false);
  const cancel = useCommand({
    run: ({ reason, version }: { reason: string; version: number }) =>
      unwrap(
        api.POST('/api/v1/orgs/{orgId}/runs/{runId}/cancel', {
          params: { path: { orgId, runId }, header: { 'If-Match': ifMatch(version), 'Idempotency-Key': newIdempotencyKey() } },
          body: { reason },
        }),
      ),
    success: 'Run cancelled. No further results accepted.',
    invalidate: [orgKey(orgId, 'runs')],
    onSuccess: () => setCancelOpen(false),
  });
  return (
    <div className="stack">
      <QueryState query={run}>
        {(r) => {
          const open = r.state === 'queued' || r.state === 'running';
          return (
            <>
              <PageHeader
                title="Agent run"
                breadcrumb={<Link to="/app/settings/agents/runs">Agent runs</Link>}
                meta={
                  <div className="row">
                    <IdChip id={r.id} />
                    <StatusDot domain="run" value={r.state} labelled />
                    {r.outcome && <span className="meta">Outcome: {enumLabel(r.outcome)}</span>}
                  </div>
                }
                actions={
                  open ? (
                    <Button variant="danger" onClick={() => setCancelOpen(true)}>
                      Cancel run
                    </Button>
                  ) : undefined
                }
              />
              <div className="split">
                <Section title="Run">
                  <DescriptionList
                    items={[
                      ['Kind', enumLabel(r.kind)],
                      ['Account', <IdChip key="a" id={r.actor_id} />],
                      ['Location', <ObjectLink key="l" kind="location" id={r.location_id} />],
                      ['Conversation', <ObjectLink key="c" kind="conversation" id={r.conversation_id} />],
                      ['Session', <ObjectLink key="s" kind="session" id={r.session_id} />],
                      ['Job', r.job_id ? <IdChip key="j" id={r.job_id} /> : <Unknown key="j">None</Unknown>],
                      ['Started', <Time key="st" value={r.started_at} emptyLabel="Not started" />],
                      ['Ended', <Time key="en" value={r.ended_at} emptyLabel="Not ended" />],
                      ['Cancellation reason', r.cancellation_reason ?? <Unknown key="cr">None</Unknown>],
                    ]}
                  />
                </Section>
                <Section title="Usage and inputs">
                  <DescriptionList
                    items={[
                      ['Tokens', r.usage ? formatNumber(r.usage.tokens) : <Unknown key="tk">Not reported</Unknown>],
                      ['Tool calls', r.usage ? formatNumber(r.usage.tool_calls) : <Unknown key="tc">Not reported</Unknown>],
                      ['Duration', r.usage ? `${formatNumber(r.usage.duration_ms)} ms` : <Unknown key="du">Not reported</Unknown>],
                      ['Actual cost', r.actual_cost ? `${r.actual_cost} ${r.cost_currency ?? ''}` : <Unknown key="cost" />],
                      ['Context used', <IdChip key="m" id={r.context_manifest_id} />],
                      ['Settings version', <IdChip key="sr" id={r.effective_settings_revision_id} />],
                      ['Prompt versions', r.prompt_revision_ids.length ? r.prompt_revision_ids.map((p) => <IdChip key={p} id={p} />) : 'None'],
                      ['Outputs', r.output_object_ids.length ? r.output_object_ids.map((p) => <IdChip key={p} id={p} />) : 'None'],
                      ['Transcript', r.trace.transcript_ref ? <span key="tr" className="mono small">{r.trace.transcript_ref}</span> : <Unknown key="tr">None recorded</Unknown>],
                      ['Runtime run', r.trace.hermes_run_id ? <span key="hr" className="mono small">{r.trace.hermes_run_id}</span> : <Unknown key="hr">None recorded</Unknown>],
                    ]}
                  />
                </Section>
              </div>
              <ConfirmDialog
                open={cancelOpen}
                onClose={() => setCancelOpen(false)}
                title="Cancel run"
                danger
                confirmLabel="Cancel run"
                pending={cancel.isPending}
                onConfirm={(reason) => cancel.mutate({ reason, version: r.version })}
                effects={[
                  'The run is marked cancelled with your reason.',
                  'Jobs stop accepting results; late results are rejected.',
                  'No mail sent or recalled.',
                ]}
              />
            </>
          );
        }}
      </QueryState>
    </div>
  );
}

// ---------------- Sessions ----------------

export interface SessionsSearch {
  role?: Schemas['AgentSessionRole'];
  state?: Schemas['AgentSessionState'];
}

export function validateSessionsSearch(s: Record<string, unknown>): SessionsSearch {
  return {
    role: SESSION_ROLES.includes(s.role as never) ? (s.role as SessionsSearch['role']) : undefined,
    state: SESSION_STATES.includes(s.state as never) ? (s.state as SessionsSearch['state']) : undefined,
  };
}

export function SessionsPage({ search, onSearch }: { search: SessionsSearch; onSearch: (s: SessionsSearch) => void }) {
  const orgId = useOrgId();
  const navigate = useNavigate();
  const { pager, query } = usePagedList<Session>(orgKey(orgId, 'agent-sessions', search), (q, signal) =>
    unwrap(api.GET('/api/v1/orgs/{orgId}/agent-sessions', { params: { path: { orgId }, query: { ...q, role: search.role, state: search.state } }, signal })),
  );
  const columns: ColumnDef<Session, unknown>[] = [
    { id: 'id', header: 'Session', cell: ({ row }) => <IdChip id={row.original.id} />, meta: { pinned: true } },
    { id: 'role', header: 'Role', cell: ({ row }) => enumLabel(row.original.role) },
    { id: 'state', header: 'State', cell: ({ row }) => <StatusDot domain="session" value={row.original.state} /> },
    { id: 'runtime', header: 'Runtime', cell: ({ row }) => <span className="mono small">{row.original.runtime_name}</span> },
    { id: 'turn', header: 'Last turn', cell: ({ row }) => <span className="num">{row.original.last_committed_turn}</span> },
    { id: 'location', header: 'Location', cell: ({ row }) => <ObjectLink kind="location" id={row.original.location_id} /> },
    { id: 'conversation', header: 'Conversation', cell: ({ row }) => <ObjectLink kind="conversation" id={row.original.conversation_id} /> },
    { id: 'updated', header: 'Updated', cell: ({ row }) => <Time value={row.original.updated_at} /> },
  ];
  return (
    <div className="stack">
      <p className="small muted">Long running agent sessions and saved state status.</p>
      <DataTable
        caption="Agent sessions"
        columns={columns}
        getRowId={(r) => r.id}
        query={query}
        pager={pager}
        sort={[]}
        onSortChange={() => undefined}
        onOpenRow={(r) => void navigate({ to: '/app/settings/agents/sessions/$sessionId', params: { sessionId: r.id } })}
        rowLabel={(r) => `${enumLabel(r.role)} session ${r.id}`}
        toolbar={
          <div className="settings-filters">
            <Select
              aria-label="Role"
              className="settings-filter"
              options={options(SESSION_ROLES, 'All roles')}
              value={search.role ?? ''}
              onChange={(e) => onSearch({ ...search, role: (e.target.value || undefined) as SessionsSearch['role'] })}
            />
            <Select
              aria-label="State"
              className="settings-filter"
              options={options(SESSION_STATES, 'All states')}
              value={search.state ?? ''}
              onChange={(e) => onSearch({ ...search, state: (e.target.value || undefined) as SessionsSearch['state'] })}
            />
          </div>
        }
        empty={<EmptyState title="No sessions match." />}
      />
    </div>
  );
}

export function SessionDetailPage({ sessionId }: { sessionId: string }) {
  const orgId = useOrgId();
  const detail = useQuery({
    queryKey: orgKey(orgId, 'agent-sessions', 'detail', sessionId),
    queryFn: ({ signal }) => unwrap(api.GET('/api/v1/orgs/{orgId}/agent-sessions/{sessionId}', { params: { path: { orgId, sessionId } }, signal })),
  });
  const [recoverOpen, setRecoverOpen] = useState(false);
  return (
    <div className="stack">
      <QueryState query={detail}>
        {({ session: s, recent_turns }) => (
          <>
            <PageHeader
              title="Agent session"
              breadcrumb={<Link to="/app/settings/agents/sessions">Agent sessions</Link>}
              meta={
                <div className="row">
                  <IdChip id={s.id} />
                  <StatusDot domain="session" value={s.state} labelled />
                </div>
              }
              actions={
                s.state === 'checkpoint_lost' ? (
                  <Button variant="primary" onClick={() => setRecoverOpen(true)}>
                    Recover session
                  </Button>
                ) : undefined
              }
            />
            {s.state === 'checkpoint_lost' && (
              <Banner tone="accent" title="Saved state lost">
                Saved state lost by the agent runtime. New turns refused until a rebuilt checkpoint is recorded.
              </Banner>
            )}
            <div className="split">
              <Section title="Session">
                <DescriptionList
                  items={[
                    ['Role', enumLabel(s.role)],
                    ['Runtime', <span key="r" className="mono">{`${s.runtime_name} / ${s.runtime_instance}`}</span>],
                    ['External session', <span key="e" className="mono">{s.external_session_ref}</span>],
                    ['Location', <ObjectLink key="l" kind="location" id={s.location_id} />],
                    ['Conversation', <ObjectLink key="c" kind="conversation" id={s.conversation_id} />],
                    ['Last committed turn', String(s.last_committed_turn)],
                  ]}
                />
              </Section>
              <Section title="Checkpoint">
                <DescriptionList
                  items={[
                    ['Reference', s.checkpoint_ref ? <span key="cr" className="mono">{s.checkpoint_ref}</span> : <Unknown key="cr">None recorded</Unknown>],
                    ['SHA 256', s.checkpoint_sha256 ? <span key="cs" className="mono small">{s.checkpoint_sha256}</span> : <Unknown key="cs">None recorded</Unknown>],
                    ['Updated', <Time key="u" value={s.updated_at} />],
                  ]}
                />
              </Section>
            </div>
            <Section title="Recent turns">
              {recent_turns.length === 0 ? (
                <EmptyState compact title="No turns yet." />
              ) : (
                <Timeline
                  label="Recent turns"
                  entries={recent_turns.map((t) => ({
                    id: t.id,
                    at: t.updated_at,
                    title: `Turn ${t.turn_number}`,
                    tone: t.state === 'failed' ? 'accent' : 'neutral',
                    meta: (
                      <span className="row">
                        <span className="meta">{enumLabel(t.state)}</span>
                        <ObjectLink kind="run" id={t.run_id}>
                          Run
                        </ObjectLink>
                      </span>
                    ),
                    body: (
                      <span className="xsmall muted">
                        {t.input_message_ids.length} input messages{t.checkpoint_sha256 ? `, checkpoint ${t.checkpoint_sha256.slice(0, 12)}` : ''}
                      </span>
                    ),
                  }))}
                />
              )}
            </Section>
            <RecoverDialog open={recoverOpen} onClose={() => setRecoverOpen(false)} session={s} />
          </>
        )}
      </QueryState>
    </div>
  );
}

function RecoverDialog({ open, onClose, session }: { open: boolean; onClose: () => void; session: Session }) {
  const orgId = useOrgId();
  const [manifest, setManifest] = useState('');
  const [ref, setRef] = useState(session.external_session_ref);
  const [ckRef, setCkRef] = useState('');
  const [sha, setSha] = useState('');
  const [resume, setResume] = useState<'active' | 'paused'>('paused');
  const recover = useCommand({
    run: (reason: string) =>
      unwrap(
        api.POST('/api/v1/orgs/{orgId}/agent-sessions/{sessionId}/recover', {
          params: {
            path: { orgId, sessionId: session.id },
            header: { 'If-Match': ifMatch(session.version), 'Idempotency-Key': newIdempotencyKey() },
          },
          body: {
            reconstruction_manifest_id: manifest.trim(),
            external_session_ref: ref.trim(),
            checkpoint_ref: ckRef.trim(),
            checkpoint_sha256: sha.trim().toLowerCase(),
            resume_state: resume,
            reason,
          },
        }),
      ),
    success: 'Session recovered.',
    invalidate: [orgKey(orgId, 'agent-sessions')],
    onSuccess: onClose,
  });
  const complete = manifest.trim() && ref.trim() && ckRef.trim() && /^[0-9a-fA-F]{64}$/.test(sha.trim());
  const fields: ReactNode = (
    <div className="stack">
      <Field label="Context ID" required>
        <TextInput className="mono" value={manifest} onChange={(e) => setManifest(e.target.value)} />
      </Field>
      <Field label="External session reference" required>
        <TextInput className="mono" value={ref} onChange={(e) => setRef(e.target.value)} />
      </Field>
      <Field label="Checkpoint reference" required>
        <TextInput className="mono" value={ckRef} onChange={(e) => setCkRef(e.target.value)} />
      </Field>
      <Field label="Checkpoint SHA 256" required hint="64 hexadecimal characters.">
        <TextInput className="mono" value={sha} onChange={(e) => setSha(e.target.value)} />
      </Field>
      <Field label="Resume as">
        <Select
          value={resume}
          onChange={(e) => setResume(e.target.value as 'active' | 'paused')}
          options={[
            { value: 'paused', label: 'Paused (safer)' },
            { value: 'active', label: 'Active' },
          ]}
        />
      </Field>
    </div>
  );
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      title="Recover agent session"
      confirmLabel={complete ? 'Recover session' : 'Fill all fields'}
      pending={recover.isPending}
      onConfirm={(reason) => complete && recover.mutate(reason)}
      effects={[
        'The session records the rebuilt checkpoint you enter.',
        `Resumes as ${enumLabel(resume).toLowerCase()}. Queued turns continue only if active.`,
        'No mail sent.',
      ]}
    >
      {fields}
    </ConfirmDialog>
  );
}

// ---------------- Jobs ----------------

export interface JobsSearch {
  kind?: Schemas['JobKind'];
  state?: Schemas['JobState'];
}

export function validateJobsSearch(s: Record<string, unknown>): JobsSearch {
  return {
    kind: JOB_KINDS.includes(s.kind as never) ? (s.kind as JobsSearch['kind']) : undefined,
    state: JOB_STATES.includes(s.state as never) ? (s.state as JobsSearch['state']) : undefined,
  };
}

type JobAction = { kind: 'cancel' | 'retry' | 'defer'; job: Job };

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function JobsPage({ search, onSearch }: { search: JobsSearch; onSearch: (s: JobsSearch) => void }) {
  const orgId = useOrgId();
  const { pager, query } = usePagedList<Job>(orgKey(orgId, 'jobs', search), (q, signal) =>
    unwrap(api.GET('/api/v1/orgs/{orgId}/jobs', { params: { path: { orgId }, query: { ...q, kind: search.kind, state: search.state } }, signal })),
  );
  const [action, setAction] = useState<JobAction | null>(null);
  const columns: ColumnDef<Job, unknown>[] = [
    { id: 'id', header: 'Job', cell: ({ row }) => <IdChip id={row.original.id} />, meta: { pinned: true } },
    { id: 'kind', header: 'Kind', cell: ({ row }) => enumLabel(row.original.kind) },
    { id: 'state', header: 'State', cell: ({ row }) => <StatusDot domain="job" value={row.original.state} /> },
    { id: 'subject', header: 'Subject', cell: ({ row }) => <IdChip id={row.original.subject_object_id} /> },
    { id: 'due', header: 'Due', cell: ({ row }) => <Time value={row.original.due_at} /> },
    { id: 'attempts', header: 'Attempts', cell: ({ row }) => <span className="num">{`${row.original.attempt_count} of ${row.original.max_attempts}`}</span> },
    {
      id: 'error',
      header: 'Last error',
      cell: ({ row }) => (row.original.last_result?.error_code ? <span className="mono small">{row.original.last_result.error_code}</span> : <span className="faint">None</span>),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const j = row.original;
        const open = j.state === 'pending' || j.state === 'leased' || j.state === 'blocked';
        return (
          <Menu
            label={`Actions for job ${j.id}`}
            iconOnly
            icon="more"
            size="sm"
            align="right"
            items={[
              { label: 'Defer', icon: 'clock', disabled: j.state !== 'pending' && j.state !== 'blocked', onSelect: () => setAction({ kind: 'defer', job: j }) },
              { label: 'Retry', icon: 'refresh', disabled: j.state !== 'failed' && j.state !== 'blocked', onSelect: () => setAction({ kind: 'retry', job: j }) },
              { label: 'Cancel', icon: 'x', danger: true, disabled: !open, onSelect: () => setAction({ kind: 'cancel', job: j }) },
            ]}
          />
        );
      },
      meta: { width: 56 },
    },
  ];
  return (
    <div className="stack">
      <p className="small muted">Work waiting for or claimed by outside runners.</p>
      <DataTable
        caption="Jobs"
        columns={columns}
        getRowId={(r) => r.id}
        query={query}
        pager={pager}
        sort={[]}
        onSortChange={() => undefined}
        toolbar={
          <div className="settings-filters">
            <Select
              aria-label="Kind"
              className="settings-filter"
              options={options(JOB_KINDS, 'All kinds')}
              value={search.kind ?? ''}
              onChange={(e) => onSearch({ ...search, kind: (e.target.value || undefined) as JobsSearch['kind'] })}
            />
            <Select
              aria-label="State"
              className="settings-filter"
              options={options(JOB_STATES, 'All states')}
              value={search.state ?? ''}
              onChange={(e) => onSearch({ ...search, state: (e.target.value || undefined) as JobsSearch['state'] })}
            />
          </div>
        }
        empty={<EmptyState title="No jobs match." />}
      />
      {action && <JobActionDialog action={action} onClose={() => setAction(null)} />}
    </div>
  );
}

function JobActionDialog({ action, onClose }: { action: JobAction; onClose: () => void }) {
  const orgId = useOrgId();
  const { job } = action;
  const [due, setDue] = useState(() => toLocalInput(new Date(Date.now() + 3600_000)));
  const header = () => ({ 'If-Match': ifMatch(job.version), 'Idempotency-Key': newIdempotencyKey() });
  const path = { orgId, jobId: job.id };
  const cmd = useCommand({
    run: async (reason: string) => {
      if (action.kind === 'cancel') {
        const r = await unwrap(api.POST('/api/v1/orgs/{orgId}/jobs/{jobId}/cancel', { params: { path, header: header() }, body: { reason } }));
        return `Job cancelled. ${r.invalidated_claim_count} tasks in progress released, ${r.cancelled_dependent_job_ids.length} dependent jobs cancelled.`;
      }
      if (action.kind === 'retry') {
        await unwrap(api.POST('/api/v1/orgs/{orgId}/jobs/{jobId}/retry', { params: { path, header: header() }, body: { reason } }));
        return 'Job queued for retry.';
      }
      await unwrap(
        api.POST('/api/v1/orgs/{orgId}/jobs/{jobId}/defer', { params: { path, header: header() }, body: { reason, due_at: new Date(due).toISOString() } }),
      );
      return 'Job deferred.';
    },
    success: (msg) => msg,
    invalidate: [orgKey(orgId, 'jobs')],
    onSuccess: onClose,
  });
  const effects: Record<JobAction['kind'], string[]> = {
    cancel: [
      `The ${enumLabel(job.kind)} job stops and any work in progress is released.`,
      'Dependent queued jobs are cancelled too and listed in the result.',
      'Mail already handed to mail sync is not recalled.',
    ],
    retry: [`The ${enumLabel(job.kind)} job goes back in the queue for another attempt.`, `Attempts so far: ${job.attempt_count} of ${job.max_attempts}.`],
    defer: [`The job will not start before the time you choose.`, 'Queue position otherwise kept.'],
  };
  const titles = { cancel: 'Cancel job', retry: 'Retry job', defer: 'Defer job' };
  return (
    <ConfirmDialog
      open
      onClose={onClose}
      title={titles[action.kind]}
      danger={action.kind === 'cancel'}
      confirmLabel={titles[action.kind]}
      pending={cmd.isPending}
      onConfirm={(reason) => cmd.mutate(reason)}
      effects={effects[action.kind]}
    >
      {action.kind === 'defer' && (
        <Field label="Not before" required hint="Your local time.">
          <TextInput type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} />
        </Field>
      )}
    </ConfirmDialog>
  );
}

// ---------------- Section frame ----------------

const AGENT_VIEWS = [
  { to: '/app/settings/agents', label: 'Keys and identities', exact: true },
  { to: '/app/settings/agents/runs', label: 'Runs' },
  { to: '/app/settings/agents/sessions', label: 'Sessions' },
  { to: '/app/settings/agents/jobs', label: 'Jobs' },
] as const;

/** Settings, API keys and agents: personal keys, machine identities and read-only agent work. */
export function AgentsSection() {
  return (
    <div className="stack-lg">
      <PageHeader title="API keys and agents" subtitle="Keys, agent identities and the work agents do." />
      <nav className="tabs" aria-label="API keys and agents">
        {AGENT_VIEWS.map((v) => (
          <Link
            key={v.to}
            to={v.to}
            className="tab"
            activeOptions={{ exact: 'exact' in v }}
            activeProps={{ 'aria-current': 'page' }}
          >
            {v.label}
          </Link>
        ))}
      </nav>
      <Outlet />
    </div>
  );
}

export function KeysAndIdentitiesPage() {
  return (
    <div className="stack-lg">
      <ApiKeysPage />
      <ActorsPage />
    </div>
  );
}
